import React, { useState, useRef, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, storeActions } from '../../store'

import { SendMessageIcon, SendMessageIconReversed } from '../Icons/Miscellaneous'
import { MicrophoneIcon } from '../Icons/MenuIcons'
import { Mode } from '../../models/enums'

interface SendTextProps {
  className?: string
}

const SendTextBox = ({ className }: SendTextProps): JSX.Element => {
  const dispatch = useDispatch()
  const isRTL = useSelector((state: RootState) => state.localeReducer.isRTL)
  const translation = useSelector((state: RootState) => state.localeReducer.currentTranslations)
  const disabledSpeechMode = useSelector((state: RootState) => state.retorikReducer.configuration.disableSpeechMode)
  const canFocusSendBox = useSelector((state: RootState) => state.retorikReducer.canFocusSendBox)
  const preventVirtualKeyboard = useSelector((state: RootState) => state.retorikReducer.configuration.preventVirtualKeyboard)
  const sendBoxPlaceHolder = useSelector((state: RootState) => state.retorikReducer.sendBoxPlaceHolder)
  const useOldRemote = useSelector((state: RootState) => state.retorikReducer.configuration.useOldRemote)
  const isMobile = useSelector((state: RootState) => state.viewReducer.isMobile)

  const [height, setHeight] = useState<string | undefined>()
  const [message, setMessage] = useState<string>('')
  const timerRef = useRef<NodeJS.Timer | null>(null)

  const textareaRef: React.MutableRefObject<HTMLTextAreaElement | null> = useRef(null)

  useEffect(() => {
    if (canFocusSendBox && textareaRef.current && document.activeElement !== textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [textareaRef, canFocusSendBox])

  useEffect(() => {
    if (textareaRef.current) {
      // Add focusin event to open virtual keyboard if it isn't disabled
      !preventVirtualKeyboard && textareaRef.current.addEventListener('focusin', () => navigator.virtualKeyboard?.show())
      textareaRef.current.addEventListener('focusout', () => dispatch(storeActions.utils.setUserTyping(false)))
    }

    return () => {
      dispatch(storeActions.utils.setUserTyping(false))
      timerRef?.current && clearTimeout(timerRef.current)
    }
  }, [preventVirtualKeyboard, textareaRef])

  const handleMessageChange = (): void => {
    timerRef?.current && clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      dispatch(storeActions.utils.setUserTyping(false))
    }, 1000)
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    dispatch(storeActions.utils.setUserTyping(true))
    handleMessageChange()
    setMessage(e.currentTarget.value)
    textareaRef?.current && setHeight(`${textareaRef.current.scrollHeight}px`)
  }

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === 'NumpadEnter') {
      e.preventDefault()
      e.stopPropagation()
      handleSend()
    }
  }

  const handleSend = (): void => {
    if (message?.length) {
      dispatch(storeActions.directline.sendMessage({ text: message }))
      setMessage('')
      setHeight(undefined)
      timerRef?.current && clearTimeout(timerRef.current)
      dispatch(storeActions.utils.setUserTyping(false))
    }
  }

  const handleClick = (): void => {
    dispatch(storeActions.retorik.setFromTextToVocal(true))
    dispatch(storeActions.speech.setMode(Mode.vocal))
  }

  return (
    <div
      id='retorik-framework-send-textbox'
      dir={isRTL ? 'rtl' : 'ltr'}
      className={`rf-relative rf-min-h-12 rf-px-4 rf-text-base rf-pt-3 rf-pb-3 rf-flex rf-items-center rf-border rf-border-textBoxBorder rf-rounded-8 rf-bg-truewhite rf-shadow-[inset_0_0_6px_#0000003B] ${className}`}
    >
      <textarea
        id='retorik-framework-send-textbox-textarea'
        ref={textareaRef}
        // @ts-ignore
        virtualkeyboardpolicy='manual'
        className={`rf-input-hidden rf-outline-none rf-max-h-full rf-overflow-y-clip rf-bg-truewhite rf-w-[calc(100%-4rem)] rf-resize-none rf-scrollbar-hidden ${
          isMobile && 'placeholder:rf-text-black placeholder:rf-opacity-100'
        }`}
        value={message}
        placeholder={sendBoxPlaceHolder || translation.menu.message}
        onKeyDown={(e): void => handleKeyDown(e)}
        onChange={(e): void => handleChange(e)}
        style={{
          height: height || '1.5rem'
        }}
      ></textarea>

      {/* Toggle to vocal mode button */}
      {!isMobile && !disabledSpeechMode && !useOldRemote && (
        <button className={`rf-absolute ${isRTL ? 'rf-left-12' : 'rf-right-12'} rf-h-full rf-w-8 rf-flex rf-justify-center rf-items-center`} onClick={handleClick}>
          <MicrophoneIcon className='rf-h-4 large-vertical:rf-h-5' color={'#00000080'} />
        </button>
      )}

      {/* Sending button */}
      <button
        className={`rf-absolute ${
          isRTL ? 'rf-left-2' : 'rf-right-2'
        } rf-h-8 rf-w-8 rf-pt-1 rf-pr-1 rf-flex rf-justify-center rf-items-center rf-border rf-border-secondary rf-bg-secondary rf-rounded-half`}
        onClick={handleSend}
      >
        {isRTL ? <SendMessageIconReversed className='rf-w-5' color='#FFF' /> : <SendMessageIcon className='rf-w-5' color='#FFF' />}
      </button>
    </div>
  )
}

export default SendTextBox
